import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { parentApi, teachersApi, appointmentsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Plus, X, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const DAY_LABELS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const DAY_LABELS_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ParentAppointments = () => {
  const { t, language } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [classTeachers, setClassTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ teacher_id: '', student_id: '', date: '', time: '', reason: '' });
  const [slots, setSlots] = useState([]);
  const [availableDays, setAvailableDays] = useState([]); // day_of_week int list
  const [slotMessage, setSlotMessage] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  // When the student changes, reload the teacher list restricted to that student's class.
  useEffect(() => {
    if (!form.student_id) {
      setClassTeachers([]);
      return;
    }
    const child = children.find(c => c.id === form.student_id);
    // Legacy students store the class in `class_id`, newer ones in `class_name`.
    // Accept either so the teacher filter works uniformly.
    const className = child?.class_name || child?.class_id;
    if (!className) {
      setClassTeachers([]);
      return;
    }
    setLoadingTeachers(true);
    teachersApi.forClass(className)
      .then(({ data }) => setClassTeachers(data || []))
      .catch(() => setClassTeachers([]))
      .finally(() => setLoadingTeachers(false));
    // Clear teacher/date/time when the child changes since assignments may differ.
    setForm(f => ({ ...f, teacher_id: '', date: '', time: '' }));
    setSlots([]);
    setAvailableDays([]);
    setSlotMessage('');
  }, [form.student_id, children]);

  // When teacher changes, fetch their weekly availability so we can hint the parent
  // about which days work. Independent of date selection.
  useEffect(() => {
    if (!form.teacher_id) {
      setAvailableDays([]);
      return;
    }
    appointmentsApi.getTeacherAvailability(form.teacher_id)
      .then(({ data }) => {
        const days = Array.isArray(data) ? data.map(d => d.day_of_week).sort((a, b) => a - b) : [];
        setAvailableDays(days);
      })
      .catch(() => setAvailableDays([]));
  }, [form.teacher_id]);

  useEffect(() => {
    if (form.teacher_id && form.date) {
      setLoadingSlots(true);
      setSlotMessage('');
      appointmentsApi.getAvailableSlots(form.teacher_id, form.date)
        .then(({ data }) => {
          setSlots(data?.slots || []);
          if (!data?.slots?.length && data?.message) setSlotMessage(data.message);
        })
        .catch(() => { setSlots([]); setSlotMessage(''); })
        .finally(() => setLoadingSlots(false));
    } else {
      setSlots([]);
      setSlotMessage('');
    }
  }, [form.teacher_id, form.date]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [apptRes, childrenRes] = await Promise.all([
        appointmentsApi.getAll({}),
        parentApi.getChildren()
      ]);
      setAppointments(apptRes.data || []);
      setChildren(childrenRes.data || []);
      if (childrenRes.data?.length === 1) {
        setForm((f) => ({ ...f, student_id: childrenRes.data[0].id }));
      }
    } catch {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    try {
      await appointmentsApi.create(form);
      toast.success(t('appointmentBooked'));
      setDialogOpen(false);
      setForm({ teacher_id: '', student_id: '', date: '', time: '', reason: '' });
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(language === 'fr' ? 'Annuler ce rendez-vous ?' : 'Cancel this appointment?')) return;
    try {
      await appointmentsApi.updateStatus(id, { status: 'cancelled' });
      toast.success(t('success'));
      loadAll();
    } catch {
      toast.error(t('error'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="parent-appointments-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar size={28} className="text-[#10B981]" weight="duotone" />
          <h1 className="font-outfit text-3xl font-bold text-[#0F172A]">{t('appointments')}</h1>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#10B981] hover:bg-[#059669]" data-testid="book-appt-btn">
          <Plus size={18} className="mr-2" />{t('bookAppointment')}
        </Button>
      </div>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('date')}</TableHead>
                <TableHead>{t('teacher')}</TableHead>
                <TableHead>{language === 'fr' ? 'Élève' : 'Student'}</TableHead>
                <TableHead>{t('reason')}</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell></TableRow>
              ) : (
                appointments.map((a) => (
                  <TableRow key={a.id} data-testid={`p-appt-row-${a.id}`}>
                    <TableCell className="font-mono text-sm">{a.date} {a.time}</TableCell>
                    <TableCell>{a.teacher_name}</TableCell>
                    <TableCell>{a.student_name}</TableCell>
                    <TableCell className="max-w-[220px] truncate">{a.reason}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[a.status] || ''}`}>{t(a.status)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      {['pending', 'confirmed'].includes(a.status) && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleCancel(a.id)} data-testid={`p-cancel-${a.id}`}>
                          <X size={14} className="mr-1" />{t('cancelAppointment')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit">{t('bookAppointment')}</DialogTitle></DialogHeader>
          <form onSubmit={handleBook} className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Élève' : 'Student'}</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="appt-student"><SelectValue placeholder={t('selectStudent')} /></SelectTrigger>
                <SelectContent>
                  {children.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('teacher')}</Label>
              <Select
                value={form.teacher_id}
                onValueChange={(v) => setForm({ ...form, teacher_id: v, time: '' })}
                disabled={!form.student_id || loadingTeachers}
              >
                <SelectTrigger data-testid="appt-teacher">
                  <SelectValue placeholder={
                    !form.student_id ? (language === 'fr' ? 'Choisissez d\'abord l\'élève' : 'Pick a student first') :
                    loadingTeachers ? (language === 'fr' ? 'Chargement…' : 'Loading…') :
                    (classTeachers.length ? t('selectTeacher') : (language === 'fr' ? 'Aucun enseignant assigné' : 'No teacher assigned'))
                  } />
                </SelectTrigger>
                <SelectContent>
                  {classTeachers.map(tt => (
                    <SelectItem key={tt.id} value={tt.id} data-testid={`appt-teacher-option-${tt.id}`}>
                      {tt.first_name} {tt.last_name}
                      {tt.subjects?.length ? ` — ${tt.subjects.join(', ')}` : (tt.subject ? ` (${tt.subject})` : '')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.student_id && !loadingTeachers && classTeachers.length === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="no-teachers-hint">
                  <Info size={12} />
                  {language === 'fr'
                    ? 'Aucun enseignant n\'est encore assigné à cette classe. Contactez l\'administration.'
                    : 'No teacher assigned to this class yet. Please contact administration.'}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('selectDate')}</Label>
                <Input type="date" data-testid="appt-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, time: '' })} required min={new Date().toISOString().slice(0, 10)} />
                {form.teacher_id && availableDays.length > 0 && (
                  <p className="text-[11px] text-[#64748B]" data-testid="teacher-available-days">
                    {language === 'fr' ? 'Jours dispo. :' : 'Available days:'}{' '}
                    <span className="font-semibold text-emerald-700">
                      {availableDays.map(d => (language === 'fr' ? DAY_LABELS_FR : DAY_LABELS_EN)[d]).join(', ')}
                    </span>
                  </p>
                )}
                {form.teacher_id && availableDays.length === 0 && (
                  <p className="text-[11px] text-amber-600" data-testid="teacher-no-availability">
                    {language === 'fr'
                      ? 'Cet enseignant n\'a pas encore défini ses disponibilités.'
                      : 'This teacher has not set availability yet.'}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t('selectSlot')}</Label>
                <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })} disabled={!slots.length}>
                  <SelectTrigger data-testid="appt-slot">
                    <SelectValue placeholder={
                      loadingSlots ? (language === 'fr' ? 'Chargement…' : 'Loading…') :
                      slots.length ? (language === 'fr' ? '-- Choisir --' : '-- Pick --') :
                      (slotMessage || t('noSlots'))
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {slots.map(s => <SelectItem key={s} value={s} data-testid={`appt-slot-option-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.teacher_id && form.date && !loadingSlots && slots.length === 0 && (
                  <p className="text-[11px] text-amber-600" data-testid="no-slots-hint">
                    {slotMessage || (language === 'fr'
                      ? 'Aucun créneau disponible ce jour. Choisissez un jour où l\'enseignant est disponible.'
                      : 'No slot available on this day.')}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('reason')}</Label>
              <Textarea data-testid="appt-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" data-testid="confirm-book-btn" className="bg-[#10B981] hover:bg-[#059669]" disabled={!form.teacher_id || !form.student_id || !form.date || !form.time}>
                {t('confirm')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentAppointments;
